import { useEffect, useMemo, useRef } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Highcharts from 'highcharts/highstock';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import { ExtendedOptions } from '@/components/Chart/types/highcharts';
import { EmptyState } from '@/components/EmptyState';
import Loader from '@/components/Loader';
import {
  DEFAULT_CHART_HEIGHT,
  regularChartPalette,
  TABLE_ROW_HEIGHT_PX
} from '@/constants/constants';
import { deepMerge } from '@/utils/deepMerge';
import { detectEnvironment } from '@/utils/detectEnvironment';
import { downloadJsonFileFromDatalake } from '@/utils/downloadJsonFileFromDatalake';

const environment = detectEnvironment();
const GROUP_NAME = `Meta_Go_Config${environment === 'staging' ? '_Staging' : ''}`;

type LayoutProps = {
  fullWidth?: boolean;
  height?: number;
};

type GraphStaticProps = {
  metadata?: Record<string, string>;
  layout?: LayoutProps;
};

const useChartData = (fileName?: string) => {
  return useQuery({
    queryKey: [fileName, GROUP_NAME],
    queryFn: () =>
      fileName ? downloadJsonFileFromDatalake(fileName, GROUP_NAME) : Promise.resolve(null),
    enabled: !!fileName,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity
  });
};

const GraphStatic = ({ metadata, layout }: GraphStaticProps) => {
  const theme = useTheme();
  const { chartRef } = useChartContext();

  const chartComponentRef = useRef<HighchartsReactRefObject>(null);

  // Ensure context ref sync
  useEffect(() => {
    if (chartComponentRef.current) {
      chartRef.current = chartComponentRef.current;
    }
  }, [chartRef]);

  const { height } = layout || {};
  const seriesFileName = metadata?.seriesFileName;
  const configFileName = metadata?.configFileName;

  const {
    data: chartConfig,
    isError: isChartConfigError,
    isLoading: isChartConfigLoading
  } = useChartData(configFileName);

  const {
    data: chartSeries,
    isError: isChartSeriesError,
    isLoading: isChartSeriesLoading
  } = useChartData(seriesFileName);

  /** Build chart options */
  const chartOptions = useMemo<ExtendedOptions | null>(() => {
    if (!chartConfig || !chartSeries) return null;

    const chartHeight = height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT;

    const baseOptions: ExtendedOptions = {
      accessibility: { enabled: false },
      title: { text: undefined },
      colors: regularChartPalette,
      exporting: {
        buttons: { contextButton: { enabled: false } },
        scale: 1,
        enabled: true,
        filename: 'GX-Chart',
        chartOptions: {
          legend: {
            enabled: true,
            itemStyle: {
              color: theme.palette.text.primary,
              fontSize: '0.8em',
              textOverflow: 'ellipsis'
            }
          },
          scrollbar: { enabled: false }
        }
      },
      tooltip: { enabled: false },
      stockTools: { gui: { enabled: false } },
      legend: { enabled: true, itemStyle: { color: theme.palette.text.primary } },
      credits: { enabled: false },
      xAxis: { labels: { style: { color: theme.palette.text.primary } } },
      yAxis: {
        title: { text: '' },
        labels: { style: { color: theme.palette.text.primary } }
      },
      series: chartSeries,
      chart: {
        reflow: true,
        animation: false,
        backgroundColor: theme.palette.background.paper,
        height: chartHeight,
        style: {
          color: theme.palette.text.primary,
          fontFamily: 'Cairo, sans-serif'
        }
      }
    };

    const merged = deepMerge(baseOptions, chartConfig);

    // Handle sankey formatter safely
    if (merged.chart?.type === 'sankey' && merged.tooltip?.formatter) {
      try {
        merged.tooltip.formatter = eval(`(${merged.tooltip.formatter})`);
      } catch {
        console.warn('Invalid sankey tooltip formatter');
      }
    }

    return merged;
  }, [chartConfig, chartSeries, height, theme]);

  /** Loading & Error states */
  if (isChartSeriesLoading || isChartConfigLoading) {
    return (
      <Box sx={{ height: DEFAULT_CHART_HEIGHT }}>
        <Loader />
      </Box>
    );
  }

  if (isChartSeriesError || isChartConfigError) {
    return <EmptyState />;
  }
  const hasAnyData =
    Array.isArray(chartSeries) && chartSeries.some(s => Array.isArray(s.data) && s.data.length > 0);
  if (!hasAnyData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '372px',
          textAlign: 'center',
          color: 'text.secondary',
          flex: 1
        }}
      >
        <Typography variant='body1'>No data available for selected time period</Typography>
      </Box>
    );
  }

  /** Render chart */
  if (chartOptions) {
    return (
      <HighchartsReact
        ref={chartComponentRef as any}
        constructorType='chart'
        highcharts={Highcharts}
        options={chartOptions}
      />
    );
  }

  return null;
};

export default GraphStatic;
